import { ConfigWindow } from '../../components';
import UseCaseLayout from './UseCaseLayout';
import type { UseCaseData } from './UseCaseLayout';

const data: UseCaseData = {
  title: 'For AI agent developers',
  subtitle:
    'Your agent config is the place API keys go to be forgotten about. Statio replaces every one of them with a single Statio key you can revoke, and the real credentials stay encrypted in Statio.',
  art: 'cascade',
  problemTitle: 'Your agent configs are a security liability.',
  problemText:
    'Every agent config file has raw API keys. You have 15 different STRIPE_KEY, GITHUB_TOKEN, SLACK_TOKEN values scattered across agent configs. One leaked config and every key is compromised. Rotating a single key means redeploying every agent that uses it.',
  solutionTitle: 'Statio is the checkpoint.',
  solutionText:
    'Statio stores every credential encrypted. Your agents get one Statio key that reaches every API you have access to. Keys are never handed to an agent. Rotate any credential instantly without touching a single agent config.',
  benefits: [
    {
      title: 'One token replaces dozens of scattered API keys',
      body: 'The config on each machine holds one revocable Statio key. Your Stripe, GitHub and Slack secrets are encrypted at rest with AES-256-GCM and added inside the gateway at call time, so the agent never sees them.',
    },
    {
      title: 'Each agent gets its own identity',
      body: 'An agent authenticates as itself, never on your personal token. Suspend or revoke one without touching anything else, and the log names the agent rather than you.',
    },
    {
      title: 'Turn off one tool, keep the rest',
      body: 'Grant an agent a whole server, then deny the tools it should never call. A tool-level deny overrides the server grant, so reading rows does not come with deleting them.',
    },
    {
      title: 'Instant credential rotation, with no agent redeployment',
      body: 'The key is stored in Statio, not in the agent, so rotating it changes nothing in any config file.',
    },
    {
      title: 'Works with Claude, Cursor, VS Code and other MCP clients',
      body: (
        <>
          The desktop app writes the config for Claude Code, Claude Desktop, Cursor, Windsurf, VS Code, Cline,
          Continue.dev, Zed and Amazon Q in one click. From a terminal, <code>mcphub config</code> does the same.
        </>
      ),
    },
    {
      title: 'Catalog servers, or bring your own OpenAPI spec',
      body: 'Install Stripe, GitHub, Slack and more from the catalog, or give Statio an OpenAPI spec and it generates, builds and hosts the server, on every plan including Free.',
      href: '/mcp-gen',
      linkLabel: 'About mcp-gen',
    },
    {
      title: 'A record of every tool call, per agent',
      body: 'Activity lists each call through the gateway with the agent or person behind it, the tool, the server, the latency and the status code. A refused call shows the 403 and the reason.',
    },
  ],
  screen: {
    title: 'What changes in the config file',
    lede: 'Before Statio, each AI client holds its own copy of every key. After, it holds one Statio key. Illustrative data.',
    window: <ConfigWindow />,
  },
  otherCases: [
    {
      label: 'For API providers',
      slug: 'providers',
      body: 'Publish your API to agents without building MCP hosting, auth and rate limiting yourself.',
    },
    {
      label: 'For enterprise security',
      slug: 'enterprise',
      body: 'Grant access per tool, keep the record of every call, and revoke any agent on its own.',
    },
  ],
};

export default function DevelopersPage() {
  return <UseCaseLayout data={data} />;
}
